/**
 * useTimeframe — Shared hook for dashboard timeframe filtering.
 *
 * Returns a dateRange { start: Date, end: Date } or null (all time).
 * All dashboard metrics should filter through this dateRange.
 */
import { useState, useMemo } from 'react';

function startOfWeek(date) {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay()); // Sunday
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfWeek(date) {
  const d = startOfWeek(date);
  d.setDate(d.getDate() + 6);
  d.setHours(23, 59, 59, 999);
  return d;
}

function startOfMonth(date) {
  const d = new Date(date);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfMonth(date) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + 1, 0); // last day of current month
  d.setHours(23, 59, 59, 999);
  return d;
}

export const TIMEFRAME_OPTIONS = [
  { key: 'all_time', label: 'All Time' },
  { key: 'this_week', label: 'This Week' },
  { key: 'this_month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'custom', label: 'Custom' },
];

export function useTimeframe(defaultPreset = 'all_time') {
  const [preset, setPreset] = useState(defaultPreset);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const dateRange = useMemo(() => {
    const now = new Date();
    switch (preset) {
      case 'this_week':
        return { start: startOfWeek(now), end: now };
      case 'last_week': {
        const lastWeek = new Date(now);
        lastWeek.setDate(lastWeek.getDate() - 7);
        return { start: startOfWeek(lastWeek), end: endOfWeek(lastWeek) };
      }
      case 'this_month':
        return { start: startOfMonth(now), end: now };
      case 'last_month': {
        const lastMonth = new Date(now);
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
      }
      case 'custom': {
        if (!customStart || !customEnd) return null;
        const s = new Date(customStart);
        const e = new Date(customEnd);
        e.setHours(23, 59, 59, 999);
        return isNaN(s.getTime()) || isNaN(e.getTime()) ? null : { start: s, end: e };
      }
      default:
        return null; // all_time = no filter
    }
  }, [preset, customStart, customEnd]);

  return { preset, setPreset, dateRange, customStart, setCustomStart, customEnd, setCustomEnd };
}
